import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Copy, Check, ExternalLink, Trash2, ChevronRight, ChevronDown, Plus, ListChecks, X, Eye, Pencil, ShoppingBag, Home, DollarSign, ImageOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  listBreakdowns, createBreakdown, updateBreakdown, deleteBreakdown, getBreakdownAdmin,
} from '@/lib/api';

const BASE = typeof window !== 'undefined' ? window.location.origin : '';
// Prefer the branded share domain for URLs the customer will actually see.
// Falls back to the current origin (admin/preview host) when not configured.
const SHARE_HOST = (process.env.REACT_APP_SHARE_DOMAIN || '').trim().toLowerCase();
const PUBLIC_BASE = SHARE_HOST ? `https://${SHARE_HOST}` : BASE;
const fmt = (n) => {
  const v = Math.round(Number(n || 0));
  return `$${v.toLocaleString('en-US')}`;
};

/**
 * Builds initial rooms selection payload from a live project — all rooms and
 * items included by default. Used when starting a fresh breakdown.
 */
const buildInitialRooms = (project) => (project?.rooms || []).map(r => ({
  room_id: r.id,
  included: true,
  item_overrides: (r.items || []).map(i => ({
    item_id: i.id,
    included: true,
    quantity: 1,
  })),
}));

/**
 * Merge a saved breakdown's rooms config against the live project so any
 * newly added rooms/items appear as (included=true) by default and stale ids
 * are dropped. Preserves the admin's earlier exclusions and quantities.
 */
const reconcileRooms = (project, savedRooms) => {
  const savedByRoom = new Map((savedRooms || []).map(r => [r.room_id, r]));
  return (project?.rooms || []).map(r => {
    const saved = savedByRoom.get(r.id);
    const savedItemsById = new Map(((saved?.item_overrides) || []).map(i => [i.item_id, i]));
    return {
      room_id: r.id,
      included: saved ? !!saved.included : true,
      item_overrides: (r.items || []).map(i => {
        const s = savedItemsById.get(i.id);
        return {
          item_id: i.id,
          included: s ? !!s.included : true,
          quantity: s?.quantity || 1,
        };
      }),
    };
  });
};

/**
 * Cost Breakdown Wizard
 *
 * Two-pane dialog:
 *  - Left: list of saved breakdowns for this project (each has its own
 *    shareable URL). "+ New Breakdown" spins up a fresh selection payload.
 *  - Right: editor for the current breakdown (title, subtitle, custom
 *    message, per-room/per-item include toggles, display-column switches,
 *    live totals) and the shareable URL for saved ones.
 */
export const BreakdownWizardDialog = ({ open, onOpenChange, project }) => {
  const [breakdowns, setBreakdowns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null); // { id?, token?, title, subtitle, message, rooms, display_options }
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [expandedRooms, setExpandedRooms] = useState({});
  // 'edit' shows the full form; 'preview' renders a compact live preview
  // mirroring what the public /p/:token page will look like, without the
  // user needing to open the link.
  const [mode, setMode] = useState('edit');
  // Off by default — the wizard renders room-level toggles only. Flip on to
  // reveal per-item include/exclude checkboxes and quantity inputs.
  const [showItemControls, setShowItemControls] = useState(false);

  const projectId = project?.id;

  const reload = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const list = await listBreakdowns(projectId);
      setBreakdowns(list);
    } catch { toast.error('Failed to load breakdowns'); }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      reload();
      setCurrent(null);
      setExpandedRooms({});
    }
  }, [open, projectId]);

  const startNew = () => {
    setCurrent({
      title: project?.name || '',
      subtitle: project?.address || '',
      message: '',
      rooms: buildInitialRooms(project),
      display_options: {
        show_budget: false,
        show_specifications: false,
        show_notes: false,
        show_links: false,
        show_status: false,
      },
    });
    // Collapse rooms by default — the compact view is room-only. Users can
    // expand individual rooms once they've flipped on item-level controls.
    setExpandedRooms({});
    setShowItemControls(false);
  };

  const openExisting = async (bd) => {
    try {
      const full = await getBreakdownAdmin(projectId, bd.id);
      setCurrent({
        id: full.id,
        token: full.token,
        title: full.title || '',
        subtitle: full.subtitle || '',
        message: full.message || '',
        rooms: reconcileRooms(project, full.rooms),
        display_options: {
          show_budget: !!full.display_options?.show_budget,
          show_specifications: !!full.display_options?.show_specifications,
          show_notes: !!full.display_options?.show_notes,
          show_links: !!full.display_options?.show_links,
          show_status: !!full.display_options?.show_status,
        },
      });
      const exp = {};
      (project?.rooms || []).forEach(r => { exp[r.id] = true; });
      setExpandedRooms(exp);
      setShowItemControls(false);
    } catch { toast.error('Failed to load breakdown'); }
  };

  const toggleRoom = (roomId, included) => {
    setCurrent(c => ({
      ...c,
      rooms: c.rooms.map(r => r.room_id === roomId ? { ...r, included } : r),
    }));
  };

  const toggleItem = (roomId, itemId, included) => {
    setCurrent(c => ({
      ...c,
      rooms: c.rooms.map(r => r.room_id !== roomId ? r : {
        ...r,
        item_overrides: r.item_overrides.map(i =>
          i.item_id === itemId ? { ...i, included } : i,
        ),
      }),
    }));
  };

  const setItemQty = (roomId, itemId, qty) => {
    const q = Math.max(1, parseInt(qty, 10) || 1);
    setCurrent(c => ({
      ...c,
      rooms: c.rooms.map(r => r.room_id !== roomId ? r : {
        ...r,
        item_overrides: r.item_overrides.map(i =>
          i.item_id === itemId ? { ...i, quantity: q } : i,
        ),
      }),
    }));
  };

  const setDisplay = (key, val) => {
    setCurrent(c => ({
      ...c,
      display_options: { ...c.display_options, [key]: val },
    }));
  };

  // Live totals per room + grand total, mirroring the backend computation.
  const totals = useMemo(() => {
    if (!current || !project) return { rooms: {}, grand: 0, itemsPurchased: 0, itemsRemaining: 0, roomCount: 0, totalBudget: 0 };
    const overrideByRoom = new Map((current.rooms || []).map(r => [r.room_id, r]));
    const rooms = {};
    let grand = 0;
    let itemsPurchased = 0;
    let itemsRemaining = 0;
    let roomCount = 0;
    let totalBudget = 0;
    (project.rooms || []).forEach(r => {
      const ov = overrideByRoom.get(r.id);
      if (!ov || !ov.included) return;
      roomCount += 1;
      const itemOv = new Map((ov.item_overrides || []).map(i => [i.item_id, i]));
      let sum = 0;
      let roomBudget = 0;
      let roomPurchased = 0;
      let roomRemaining = 0;
      (r.items || []).forEach(it => {
        const iov = itemOv.get(it.id);
        if (iov && !iov.included) return;
        const price = Number(it.ordered_price || 0);
        if (price > 0) {
          sum += price;
          roomPurchased += 1;
        } else {
          roomRemaining += 1;
        }
        const b = Number(it.budget || 0);
        if (b > 0) roomBudget += b;
      });
      rooms[r.id] = { total: sum, purchased: roomPurchased, remaining: roomRemaining };
      grand += sum;
      itemsPurchased += roomPurchased;
      itemsRemaining += roomRemaining;
      totalBudget += roomBudget > 0 ? roomBudget : sum;
    });
    return { rooms, grand, itemsPurchased, itemsRemaining, roomCount, totalBudget };
  }, [current, project]);

  const save = async () => {
    if (!current || !projectId) return;
    setSaving(true);
    try {
      let saved;
      if (current.id) {
        saved = await updateBreakdown(projectId, current.id, {
          title: current.title,
          subtitle: current.subtitle,
          message: current.message,
          rooms: current.rooms,
          display_options: current.display_options,
        });
      } else {
        saved = await createBreakdown(projectId, {
          title: current.title,
          subtitle: current.subtitle,
          message: current.message,
          rooms: current.rooms,
          display_options: current.display_options,
        });
      }
      setCurrent(c => ({ ...c, id: saved.id, token: saved.token }));
      toast.success(current.id ? 'Breakdown updated' : 'Breakdown created');
      await reload();
    } catch { toast.error('Failed to save breakdown'); }
    setSaving(false);
  };

  const removeBreakdown = async (bd) => {
    if (!window.confirm('Delete this breakdown link? It will stop working immediately.')) return;
    try {
      await deleteBreakdown(projectId, bd.id);
      toast.success('Breakdown deleted');
      if (current?.id === bd.id) setCurrent(null);
      await reload();
    } catch { toast.error('Failed to delete breakdown'); }
  };

  const publicUrl = current?.token ? `${PUBLIC_BASE}/p/${current.token}` : '';

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = publicUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const projRoomsById = useMemo(
    () => Object.fromEntries((project?.rooms || []).map(r => [r.id, r])),
    [project],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 gap-0 max-h-[92vh] overflow-hidden flex flex-col" data-testid="breakdown-wizard">
        <DialogHeader className="px-6 py-4 border-b border-[#e5e0d5]">
          <DialogTitle className="font-heading text-xl text-[#1f2933] flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-[#2d948c]" />
            Shareable Cost Breakdown
          </DialogTitle>
          <p className="text-xs text-[#5b6773]">
            Craft a public summary of this project&apos;s furniture cost. Choose which rooms and items to include, edit the header, and share the link.
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[240px_1fr]">
          {/* Left rail: saved breakdowns */}
          <div className="border-r border-[#e5e0d5] bg-[#faf8f3] flex flex-col min-h-0">
            <div className="p-3 border-b border-[#e5e0d5]">
              <Button
                onClick={startNew}
                className="w-full gap-1.5 bg-[#2d948c] hover:bg-[#247a72] text-white"
                size="sm"
                data-testid="new-breakdown-btn"
              >
                <Plus className="h-4 w-4" /> New Breakdown
              </Button>
            </div>
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-2 space-y-1">
                {loading && <p className="text-xs text-[#5b6773] px-2 py-1">Loading…</p>}
                {!loading && breakdowns.length === 0 && (
                  <p className="text-xs text-[#5b6773] px-2 py-3 leading-relaxed">
                    No breakdowns yet. Click &quot;New Breakdown&quot; to create one.
                  </p>
                )}
                {breakdowns.map(bd => (
                  <div
                    key={bd.id}
                    className={`group rounded-md px-2.5 py-2 text-xs cursor-pointer transition-colors ${
                      current?.id === bd.id
                        ? 'bg-[#e6f2f0] text-[#1f2933]'
                        : 'hover:bg-[#f0ede4] text-[#3a4652]'
                    }`}
                    onClick={() => openExisting(bd)}
                    data-testid={`breakdown-list-item-${bd.id}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{bd.title || 'Untitled'}</div>
                        <div className="text-[10px] text-[#8a8375]">
                          {new Date(bd.updated_at || bd.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeBreakdown(bd); }}
                        className="opacity-0 group-hover:opacity-100 text-[#c04040] hover:text-[#a03030] p-1"
                        title="Delete breakdown"
                        data-testid={`delete-breakdown-${bd.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Right pane: editor */}
          {!current ? (
            <div className="flex items-center justify-center p-10 text-center text-sm text-[#5b6773]">
              <div>
                <ListChecks className="h-10 w-10 mx-auto text-[#c8c1b0] mb-3" />
                <p>Select a breakdown from the left to edit, or click <strong className="text-[#1f2933]">New Breakdown</strong> to create one.</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col min-h-0">
              {/* Edit / Preview tab switcher */}
              <div className="flex items-center gap-1 border-b border-[#e5e0d5] bg-[#faf8f3] px-3 flex-shrink-0">
                <button
                  onClick={() => setMode('edit')}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2 border-b-2 -mb-px transition-colors ${
                    mode === 'edit'
                      ? 'border-[#2d948c] text-[#1f2933] font-medium'
                      : 'border-transparent text-[#5b6773] hover:text-[#1f2933]'
                  }`}
                  data-testid="tab-edit"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => setMode('preview')}
                  className={`flex items-center gap-1.5 text-xs px-3 py-2 border-b-2 -mb-px transition-colors ${
                    mode === 'preview'
                      ? 'border-[#2d948c] text-[#1f2933] font-medium'
                      : 'border-transparent text-[#5b6773] hover:text-[#1f2933]'
                  }`}
                  data-testid="tab-preview"
                >
                  <Eye className="h-3.5 w-3.5" /> Preview
                </button>
                <div className="ml-auto text-[11px] text-[#8a8375] pr-1">
                  {mode === 'preview' ? 'Live preview — reflects unsaved changes' : ''}
                </div>
              </div>

              {mode === 'preview' ? (
                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-6">
                    <BreakdownPreview current={current} project={project} totals={totals} projRoomsById={projRoomsById} />
                  </div>
                </ScrollArea>
              ) : (
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-6 space-y-6">
                {/* URL / status bar for saved breakdowns */}
                {current.token && (
                  <div className="rounded-lg border border-[#c9e5e2] bg-[#e6f2f0] px-3 py-2.5 flex flex-wrap items-center gap-2">
                    <div className="text-xs font-medium text-[#1f6b64] whitespace-nowrap">Public URL:</div>
                    <Input value={publicUrl} readOnly className="flex-1 text-xs bg-white h-8 min-w-[220px]" data-testid="breakdown-url" />
                    <Button size="sm" variant="outline" onClick={copyUrl} className="h-8 gap-1" data-testid="copy-breakdown-url">
                      {copied ? <><Check className="h-3.5 w-3.5 text-[#2f7d5a]" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                    </Button>
                    <a href={publicUrl} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="h-8 gap-1" data-testid="open-breakdown-url">
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                      </Button>
                    </a>
                  </div>
                )}

                {/* Header edits */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-semibold text-[#1f2933]">Header — &quot;Furniture project for…&quot;</Label>
                    <Input
                      value={current.title}
                      onChange={e => setCurrent(c => ({ ...c, title: e.target.value }))}
                      placeholder={project?.name || 'Project name'}
                      className="mt-1"
                      data-testid="breakdown-title"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-[#1f2933]">Subtitle (address)</Label>
                    <Textarea
                      value={current.subtitle}
                      onChange={e => setCurrent(c => ({ ...c, subtitle: e.target.value }))}
                      placeholder={project?.address || 'Address'}
                      rows={2}
                      className="mt-1 resize-none"
                      data-testid="breakdown-subtitle"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-[#1f2933]">Custom message (optional)</Label>
                    <Textarea
                      value={current.message}
                      onChange={e => setCurrent(c => ({ ...c, message: e.target.value }))}
                      placeholder="Add a note for the recipient — e.g. 'Here's the full furniture cost breakdown for your review.'"
                      rows={3}
                      className="mt-1 resize-none"
                      data-testid="breakdown-message"
                    />
                  </div>
                </div>

                <Separator />

                {/* Display options */}
                <div>
                  <Label className="text-xs font-semibold text-[#1f2933] mb-2 block">Additional columns on the summary</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      ['show_budget', 'Budget'],
                      ['show_specifications', 'Specifications'],
                      ['show_notes', 'Notes'],
                      ['show_links', 'Vendor links'],
                      ['show_status', 'Status'],
                    ].map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 rounded-md border border-[#e5e0d5] px-2.5 py-1.5 cursor-pointer hover:bg-[#f7f5ee]">
                        <Switch
                          checked={!!current.display_options?.[k]}
                          onCheckedChange={v => setDisplay(k, v)}
                          data-testid={`toggle-${k}`}
                        />
                        <span className="text-xs text-[#3a4652]">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Rooms / items selection */}
                <div>
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <Label className="text-xs font-semibold text-[#1f2933]">Rooms to include</Label>
                    <div className="text-sm font-heading text-[#1f2933] flex-shrink-0">
                      Grand total: <span className="font-semibold text-[#2d948c]">{fmt(totals.grand)}</span>
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mb-2 rounded-md border border-[#e5e0d5] px-2.5 py-1.5 cursor-pointer hover:bg-[#f7f5ee]">
                    <Switch
                      checked={showItemControls}
                      onCheckedChange={setShowItemControls}
                      data-testid="toggle-item-controls"
                    />
                    <span className="text-xs text-[#3a4652]">Show individual item controls (include/exclude &amp; quantity)</span>
                  </label>
                  <div className="space-y-2">
                    {(current.rooms || []).map(rov => {
                      const room = projRoomsById[rov.room_id];
                      if (!room) return null;
                      const itemMap = new Map((rov.item_overrides || []).map(i => [i.item_id, i]));
                      const isExpanded = showItemControls && expandedRooms[rov.room_id] !== false;
                      return (
                        <div key={rov.room_id} className={`rounded-lg border ${rov.included ? 'border-[#e5e0d5]' : 'border-[#e5e0d5] opacity-60'}`}>
                          <div className="flex items-center gap-2 px-3 py-2 bg-[#faf8f3] rounded-t-lg">
                            {showItemControls ? (
                              <button
                                type="button"
                                onClick={() => setExpandedRooms(s => ({ ...s, [rov.room_id]: !isExpanded }))}
                                className="text-[#5b6773] hover:text-[#1f2933]"
                                data-testid={`expand-room-${rov.room_id}`}
                              >
                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                            ) : (
                              <span className="w-4" />
                            )}
                            <Checkbox
                              checked={rov.included}
                              onCheckedChange={v => toggleRoom(rov.room_id, !!v)}
                              data-testid={`toggle-room-${rov.room_id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-[#1f2933] truncate">{room.name}</div>
                              <div className="text-[11px] text-[#5b6773]">{(room.items || []).length} items</div>
                            </div>
                            <div className="text-sm font-medium text-[#2d948c] tabular-nums">
                              {fmt(totals.rooms[rov.room_id]?.total || 0)}
                            </div>
                          </div>

                          {isExpanded && rov.included && (
                            <div className="divide-y divide-[#f0ede4]">
                              {(room.items || []).length === 0 && (
                                <p className="text-xs text-[#8a8375] px-4 py-3">No items in this room.</p>
                              )}
                              {(room.items || []).map(it => {
                                const iov = itemMap.get(it.id) || { included: true, quantity: 1 };
                                const price = Number(it.ordered_price || 0);
                                return (
                                  <div key={it.id} className={`flex items-center gap-2 px-4 py-1.5 text-sm ${iov.included ? '' : 'opacity-50'}`}>
                                    <Checkbox
                                      checked={iov.included}
                                      onCheckedChange={v => toggleItem(rov.room_id, it.id, !!v)}
                                      data-testid={`toggle-item-${it.id}`}
                                    />
                                    <div className="flex-1 min-w-0 truncate text-[#3a4652]">
                                      {it.name || '(untitled)'}
                                      {price === 0 && (
                                        <span className="ml-2 text-[10px] uppercase tracking-wide text-[#a3801e] bg-[#fef3c7] px-1.5 py-0.5 rounded">Pending</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 text-xs text-[#5b6773]">
                                      <span>Qty</span>
                                      <input
                                        type="number"
                                        min={1}
                                        value={iov.quantity || 1}
                                        onChange={e => setItemQty(rov.room_id, it.id, e.target.value)}
                                        className="w-12 h-7 px-1 text-center border border-[#e5e0d5] rounded"
                                        data-testid={`qty-item-${it.id}`}
                                      />
                                    </div>
                                    <div className="w-20 text-right tabular-nums text-[#1f2933]">
                                      {fmt(price)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </ScrollArea>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-[#e5e0d5] bg-[#faf8f3]">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="breakdown-close">
            <X className="h-4 w-4 mr-1" /> Close
          </Button>
          {current && (
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#2d948c] hover:bg-[#247a72] text-white"
              data-testid="save-breakdown"
            >
              {saving ? 'Saving…' : current.id ? 'Save changes' : 'Create shareable link'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BreakdownWizardDialog;

/* ── BreakdownPreview ─────────────────────────────────
 * Compact live preview mirroring the public /p/:token page. Uses the
 * in-progress wizard state so admins can see the output before saving or
 * opening the actual link. All computations mirror the backend
 * `_resolve_breakdown` so the numbers match what the recipient will see.
 */
const PAL = {
  bg: '#f4efe5',
  card: '#fbf8f0',
  ink: '#1c3a2e',
  ink2: '#2d4a3d',
  mute: '#6b7969',
  brass: '#a37f2c',
  hairline: '#e7dfd0',
  chip: '#eee6d3',
};

const BreakdownPreview = ({ current, project, totals, projRoomsById }) => {
  const overrideByRoom = useMemo(() => {
    const m = new Map();
    (current?.rooms || []).forEach(r => m.set(r.room_id, r));
    return m;
  }, [current]);

  // Build resolved rooms client-side mirroring backend order.
  const rooms = useMemo(() => {
    const out = [];
    (project?.rooms || []).forEach(r => {
      const ov = overrideByRoom.get(r.id);
      if (!ov || !ov.included) return;
      const itemOv = new Map((ov.item_overrides || []).map(i => [i.item_id, i]));
      const purchased = [];
      const pending = [];
      let sum = 0;
      (r.items || []).forEach(it => {
        const iov = itemOv.get(it.id);
        if (iov && !iov.included) return;
        const price = Number(it.ordered_price || 0);
        const qty = iov?.quantity || 1;
        const entry = { id: it.id, name: it.name || '', price, quantity: qty };
        if (price > 0) {
          purchased.push(entry);
          sum += price;
        } else {
          pending.push(entry);
        }
      });
      // Room thumbnail: lowest-position mood board, matches ProjectView + backend.
      const mbs = [...(r.mood_boards || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
      out.push({
        id: r.id,
        name: r.name || '',
        purchased,
        pending,
        total: sum,
        thumbnail: mbs[0]?.url || null,
      });
    });
    return out;
  }, [project, overrideByRoom]);

  if (!current) return null;

  return (
    <div className="rounded-xl overflow-hidden border" style={{ background: PAL.bg, borderColor: PAL.hairline }} data-testid="wizard-preview">
      {/* Preview brand header */}
      <div className="px-4 py-2.5 border-b flex items-center gap-2" style={{ background: PAL.card, borderColor: PAL.hairline }}>
        <div className="h-6 w-6 rounded-full border-[1.5px] flex items-center justify-center text-[9px] font-bold" style={{ borderColor: PAL.ink, color: PAL.ink }}>FL</div>
        <div className="leading-none">
          <div className="font-heading text-[10px] tracking-[0.14em] font-semibold" style={{ color: PAL.ink }}>FLEX LIVING</div>
          <div className="font-heading text-[8px] tracking-[0.22em]" style={{ color: PAL.mute }}>MIAMI</div>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Header row */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4 items-start">
          <div className="min-w-0">
            <h3 className="font-display-serif text-2xl leading-tight break-words" style={{ color: PAL.ink }}>
              {current.title || 'Furniture Project'}
            </h3>
            {current.subtitle && (
              <p className="mt-1.5 text-xs whitespace-pre-line" style={{ color: PAL.ink2 }}>{current.subtitle}</p>
            )}
            {current.message && (
              <p className="mt-2 text-[11px] italic whitespace-pre-wrap max-w-md" style={{ color: PAL.ink2 }}>{current.message}</p>
            )}
          </div>
          <div className="rounded-lg px-3 py-3" style={{ background: PAL.card, border: `1px solid ${PAL.hairline}` }}>
            <p className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: PAL.mute }}>Project Cost</p>
            <p className="font-display-serif text-2xl leading-none mt-1 tabular-nums" style={{ color: PAL.ink }}>{fmt(totals.grand)}</p>
            <div className="mt-2 pt-2 border-t flex items-center gap-2" style={{ borderColor: PAL.hairline }}>
              <div className="h-6 w-6 rounded-full flex items-center justify-center" style={{ background: PAL.chip }}>
                <ShoppingBag className="h-3 w-3" style={{ color: PAL.ink }} />
              </div>
              <div className="leading-none">
                <div className="text-sm font-semibold tabular-nums" style={{ color: PAL.ink }}>{totals.itemsPurchased}</div>
                <div className="text-[9px]" style={{ color: PAL.mute }}>Items Purchased</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-3 gap-2">
          <MiniStat icon={DollarSign} label="Total Budget" value={fmt(totals.totalBudget || totals.grand)} />
          <MiniStat icon={ShoppingBag} label="Purchased" value={totals.itemsPurchased} suffix="Items" />
          <MiniStat icon={Home} label="Rooms" value={totals.roomCount} suffix={totals.roomCount === 1 ? 'Room' : 'Rooms'} />
        </div>

        {/* Room summary */}
        <div>
          <div className="flex items-end justify-between mb-2">
            <h4 className="font-display-serif text-lg" style={{ color: PAL.ink }}>Room Summary</h4>
            <span className="text-[10px]" style={{ color: PAL.mute }}>{rooms.length} of {rooms.length} rooms</span>
          </div>
          <div className="space-y-1.5">
            {rooms.length === 0 && (
              <p className="text-xs py-3 text-center" style={{ color: PAL.mute }}>No rooms selected.</p>
            )}
            {rooms.map(r => (
              <div key={r.id} className="rounded-md p-2 flex items-center gap-2.5" style={{ background: PAL.card, border: `1px solid ${PAL.hairline}` }}>
                <div className="h-9 w-14 rounded overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: PAL.chip }}>
                  {r.thumbnail ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/furniture-projects/files/${r.thumbnail}`}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <ImageOff className="h-3 w-3" style={{ color: PAL.mute }} />
                  )}
                </div>
                <div className="min-w-0 flex-1 grid grid-cols-[1.4fr_1fr_1fr] gap-2 items-center">
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate" style={{ color: PAL.ink }}>{r.name}</div>
                    <div className="text-[10px] tabular-nums" style={{ color: PAL.mute }}>
                      {r.total > 0 ? `${fmt(r.total)} · Room Total` : 'Pending · Room Total'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: PAL.ink }}>{r.purchased.length}</div>
                    <div className="text-[9px]" style={{ color: PAL.mute }}>Purchased</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: PAL.ink }}>
                      {r.purchased.length === 0 && r.pending.length === 0
                        ? '—'
                        : `${Math.round((r.purchased.length / (r.purchased.length + r.pending.length)) * 100)}%`}
                    </div>
                    <div className="text-[9px]" style={{ color: PAL.mute }}>{r.purchased.length === 0 ? 'Not started' : 'Complete'}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* First room items — 4-column grid preview */}
        {rooms[0] && rooms[0].purchased.length > 0 && (
          <div className="rounded-md p-3" style={{ background: PAL.card, border: `1px solid ${PAL.hairline}` }}>
            <div className="text-[9px] uppercase tracking-[0.22em] font-semibold mb-2" style={{ color: PAL.mute }}>Sample Items ({rooms[0].name})</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {rooms[0].purchased.slice(0, 8).map(it => (
                <div key={it.id} className="rounded px-1.5 py-1 text-[10px] flex flex-col gap-0.5" style={{ border: `1px solid ${PAL.hairline}`, background: 'rgba(255,255,255,0.4)' }}>
                  <span className="min-w-0 truncate font-medium leading-tight" style={{ color: PAL.ink }}>
                    {it.name}
                    {it.quantity > 1 && <span className="ml-1 text-[8px]" style={{ color: PAL.mute }}>×{it.quantity}</span>}
                  </span>
                  <span className="tabular-nums" style={{ color: PAL.ink }}>{fmt(it.price)}</span>
                </div>
              ))}
            </div>
            {rooms[0].pending.length > 0 && (
              <div className="mt-2 rounded px-2 py-1.5 text-[10px]" style={{ background: '#f4ecd3', border: `1px solid #e7d9b0` }}>
                <span className="uppercase tracking-[0.18em] font-semibold" style={{ color: PAL.brass }}>Sourcing:</span>{' '}
                <span style={{ color: PAL.ink2 }}>
                  {rooms[0].pending.slice(0, 4).map(i => i.name).join(', ')}
                  {rooms[0].pending.length > 4 && ` + ${rooms[0].pending.length - 4} more`}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const MiniStat = ({ icon: Icon, label, value, suffix }) => (
  <div className="rounded-md p-2.5" style={{ background: PAL.card, border: `1px solid ${PAL.hairline}` }}>
    <div className="h-6 w-6 rounded-full flex items-center justify-center mb-1.5" style={{ background: PAL.chip }}>
      <Icon className="h-3 w-3" style={{ color: PAL.ink }} />
    </div>
    <div className="text-[8px] uppercase tracking-[0.2em] font-semibold" style={{ color: PAL.mute }}>{label}</div>
    <div className="flex items-baseline gap-1 mt-0.5">
      <span className="font-heading text-base font-semibold tabular-nums" style={{ color: PAL.ink }}>{value}</span>
      {suffix && <span className="text-[9px]" style={{ color: PAL.mute }}>{suffix}</span>}
    </div>
  </div>
);
